import { z } from 'zod';

import { type IssuedQuizQuote } from './quizQuoteApi';

/**
 * 견적서 화면이 그릴 값만 담은 공개본.
 *
 * 토큰을 가진 사람이면 누구나 CRM 공개 조회로 받아 보므로 담당자 이름·연락처·
 * 이메일은 담지 않는다. 문서에 실제로 나오는 건 조직명과 비고뿐이다.
 */
export interface PublicQuizQuote {
  recipient: {
    organizationName: string;
    annotation?: string;
  };
  product: IssuedQuizQuote['product'];
  pricing: IssuedQuizQuote['pricing'];
  issuedAt: string;
  validUntil: string;
}

/** 발행된 견적에서 공개해도 되는 값만 뽑는다. */
export function toPublicQuizQuote(quote: IssuedQuizQuote): PublicQuizQuote {
  return {
    recipient: {
      organizationName: quote.recipient.organizationName,
      annotation: quote.recipient.annotation,
    },
    product: quote.product,
    pricing: quote.pricing,
    issuedAt: quote.issuedAt,
    validUntil: quote.validUntil,
  };
}

const publicQuizQuoteSchema = z.object({
  recipient: z.object({
    organizationName: z.string().min(1),
    annotation: z.string().optional(),
  }),
  product: z.object({
    plan: z.enum(['basic', 'pro']),
    requestedPricingType: z.enum(['individual', 'group']),
    pricingType: z.enum(['individual', 'group']),
    pricingTypeNotice: z
      .object({ code: z.string().min(1), message: z.string().min(1) })
      .nullish(),
    userCount: z.number().int().positive(),
    monthCount: z.number().int().positive(),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
  }),
  pricing: z.object({
    currency: z.literal('KRW'),
    regularUnitAmount: z.number().finite().nonnegative(),
    originalAmount: z.number().finite().nonnegative(),
    discountAmount: z.number().finite().nonnegative(),
    finalAmount: z.number().finite().nonnegative(),
    discounts: z.array(
      z.object({
        code: z.string().min(1),
        label: z.string().min(1),
        rate: z.number().finite().min(0).max(1).nullable().optional(),
        amount: z.number().finite().nonnegative(),
      }),
    ),
  }),
  issuedAt: z.string().min(1),
  validUntil: z.string().min(1),
});

/**
 * CRM 공개 조회 응답의 document 를 검증한다.
 *
 * 발행 당시 스키마로 저장된 값이라 지금 코드와 어긋날 수 있다. 금액 합계 같은
 * 교차검증(issuedQuizQuoteSchema 가 하는 superRefine)은 하지 않는다 — 과거
 * 견적을 "보여주기"만 하는 화면에서 그걸로 막으면 열람 자체가 안 된다.
 */
export function parsePublicQuizQuote(value: unknown): PublicQuizQuote {
  return publicQuizQuoteSchema.parse(value);
}
